import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material'
import { API_BASE_URL } from '../config/api'
import {
  Visibility,
  Edit,
  CheckCircle,
  Cancel,
  Person,
  RemoveCircle,
  Schedule
} from '@mui/icons-material'
import ImageOverlay from '../components/ImageOverlay'

interface Lot {
  id: string
  lot_name: string
  lot_description?: string
  farmer_name?: string
  farmer_id: string
  quantity: number
  unit: string
  reserve_price: number
  starting_price?: number
  quality_grade?: string
  status: 'draft' | 'active' | 'inactive' | 'pending_approval' | 'scheduled' | 'completed' | 'cancelled'
  auction_start_date?: string
  auction_end_date?: string
  inspection_location?: string
  pickup_location?: string
  payment_terms?: string
  delivery_terms?: string
  created_at: string
  updated_at?: string
  lot_images?: any[]
  harvest_images?: any[]
}

const Lots: React.FC = () => {
  const [lots, setLots] = useState<Lot[]>([])
  const [loading, setLoading] = useState(true)
  const [viewDialog, setViewDialog] = useState(false)
  const [editDialog, setEditDialog] = useState(false)
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null)
  const [editData, setEditData] = useState<Partial<Lot>>({})
  const [imageOverlayOpen, setImageOverlayOpen] = useState(false)
  const [overlayImages, setOverlayImages] = useState<string[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', severity: 'info' })
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; lot: Lot | null }>({ open: false, lot: null })

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({ open: true, message, severity })
  }

  const fetchLots = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      
      const response = await fetch(`${API_BASE_URL}/admin/lots`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch lots: ${response.status}`)
      }

      const result = await response.json()
      const lotData = result.data || []
      
      // All lots are already filtered by harvest_id IS NOT NULL in the backend
      const farmerLots = lotData
      
      const transformedLots: Lot[] = farmerLots.map((lot: any) => {
        // Parse images if stored as JSON string
        let lotImages = lot.lot_images || lot.images || [];
        let harvestImages = lot.harvest_images || [];
        
        if (typeof lotImages === 'string') {
          try {
            lotImages = JSON.parse(lotImages);
          } catch (e) {
            lotImages = [];
          }
        }
        
        if (typeof harvestImages === 'string') {
          try {
            harvestImages = JSON.parse(harvestImages);
          } catch (e) {
            harvestImages = [];
          }
        }
        
        return {
          id: lot.id,
          lot_name: lot.lot_name || `Lot #${lot.id}`,
          lot_description: lot.lot_description,
          farmer_name: lot.farmer?.name || lot.farmer_name || 'Unknown Farmer',
          farmer_id: lot.farmer_id,
          quantity: parseFloat(lot.quantity) || 0,
          unit: lot.unit || 'kg',
          reserve_price: parseFloat(lot.reserve_price) || 0,
          starting_price: parseFloat(lot.starting_price) || 0,
          quality_grade: lot.quality_grade || 'Standard',
          status: lot.status || 'pending_approval',
          auction_start_date: lot.auction_start_date,
          auction_end_date: lot.auction_end_date,
          inspection_location: lot.inspection_location,
          pickup_location: lot.pickup_location,
          payment_terms: lot.payment_terms,
          delivery_terms: lot.delivery_terms,
          created_at: lot.created_at || new Date().toISOString(),
          updated_at: lot.updated_at,
          lot_images: lotImages,
          harvest_images: harvestImages
        };
      })

      setLots(transformedLots)
    } catch (error) {
      console.error('Error fetching lots:', error)
      setLots([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLots()
  }, [])

  const handleViewLot = (lot: Lot) => {
    setSelectedLot(lot)
    setViewDialog(true)
  }

  const handleEditLot = (lot: Lot) => {
    setSelectedLot(lot)
    setEditData({
      lot_name: lot.lot_name,
      lot_description: lot.lot_description,
      quantity: lot.quantity,
      unit: lot.unit,
      reserve_price: lot.reserve_price,
      starting_price: lot.starting_price,
      quality_grade: lot.quality_grade,
      inspection_location: lot.inspection_location,
      pickup_location: lot.pickup_location,
      payment_terms: lot.payment_terms,
      delivery_terms: lot.delivery_terms
    })
    setEditDialog(true)
  }

  const handleUpdateLot = async () => {
    if (!selectedLot) return

    try {
      const token = localStorage.getItem('token')
      
      const response = await fetch(`${API_BASE_URL}/admin/auctions/${selectedLot.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      })

      if (!response.ok) {
        throw new Error(`Failed to update lot: ${response.status}`)
      }

      showNotification('Lot updated successfully!', 'success')
      setEditDialog(false)
      fetchLots()
    } catch (error) {
      console.error('Error updating lot:', error)
      showNotification('Failed to update lot', 'error')
    }
  }

  const handleToggleStatus = async (lot: Lot) => {
    // draft → active (activate), active → inactive (deactivate)
    const isActivating = lot.status === 'draft';
    const endpoint = isActivating ? 'activate' : 'deactivate';
    
    try {
      const token = localStorage.getItem('token')
      
      const response = await fetch(`${API_BASE_URL}/admin/auctions/${lot.id}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to ${endpoint} lot: ${response.status}`)
      }

      if (isActivating) {
        showNotification('Lot approved! Go to Auctions page → Create Auction to schedule it with dates.', 'success')
      } else {
        showNotification('Lot deactivated successfully.', 'info')
      }
      fetchLots()
    } catch (error) {
      console.error('Error updating lot status:', error)
      showNotification('Failed to update lot status', 'error')
    }
  }

  const handleRemoveFromAuctionClick = (lot: Lot) => {
    if (!lot.auction_start_date) {
      showNotification('This lot is not part of any auction', 'info')
      return
    }
    setConfirmDialog({ open: true, lot })
  }

  const handleRemoveFromAuction = async () => {
    const lot = confirmDialog.lot
    if (!lot) return
    setConfirmDialog({ open: false, lot: null })

    try {
      const token = localStorage.getItem('token')
      
      if (!token) {
        showNotification('Authentication required. Please login again.', 'error')
        window.location.href = '/login'
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/auctions/${lot.id}/remove-from-auction`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (!response.ok) {
        if (data.error?.code === 'INVALID_TOKEN' || data.error?.code === 'TOKEN_EXPIRED') {
          showNotification('Your session has expired. Please login again.', 'error')
          localStorage.removeItem('token')
          window.location.href = '/login'
          return
        }
        throw new Error(data.error?.message || `Failed to remove lot from auction: ${response.status}`)
      }

      showNotification('Lot removed from auction successfully!', 'success')
      fetchLots()
    } catch (error) {
      console.error('Error removing lot from auction:', error)
      showNotification('Failed to remove lot from auction', 'error')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'draft': return 'warning'
      case 'inactive': return 'default'
      case 'pending_approval': return 'warning'
      case 'scheduled': return 'info'
      case 'completed': return 'primary'
      case 'cancelled': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle />
      case 'inactive': return <Cancel />
      case 'draft': return <Edit />
      case 'scheduled': return <Schedule />
      default: return null
    }
  }

  const handleOpenImageOverlay = (lot: Lot, startIndex: number = 0) => {
    const images = [
      ...(lot.lot_images || []),
      ...(lot.harvest_images || [])
    ].map((img: any) => {
      const imageUrl = typeof img === 'string' ? img : img.url
      return imageUrl?.startsWith('http') ? imageUrl : `${API_BASE_URL.replace('/api/v1', '')}${imageUrl}`
    })
    
    setOverlayImages(images)
    setCurrentImageIndex(startIndex)
    setImageOverlayOpen(true)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Auction Lots Management
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lot Name</TableCell>
                  <TableCell>Images</TableCell>
                  <TableCell>Farmer</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Reserve Price</TableCell>
                  <TableCell>Quality Grade</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lots.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary">No lots found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  lots.map((lot) => (
                    <TableRow key={lot.id} hover>
                      <TableCell>{lot.lot_name}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {(lot.lot_images?.[0] || lot.harvest_images?.[0]) ? (
                            <Box
                              component="img"
                              src={(() => {
                                const firstImage = lot.lot_images?.[0] || lot.harvest_images?.[0];
                                if (!firstImage) return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f3f4f6" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E';
                                const imageUrl = typeof firstImage === 'object' && firstImage.url ? firstImage.url : firstImage;
                                return imageUrl?.startsWith('http') ? imageUrl : `${API_BASE_URL.replace('/api/v1', '')}${imageUrl}`;
                              })()}
                              alt={lot.lot_name}
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid #ddd',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 }
                              }}
                              onClick={() => handleOpenImageOverlay(lot, 0)}
                              onError={(e: any) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40"%3E%3Crect fill="%23f3f4f6" width="40" height="40"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="8"%3ENo Image%3C/text%3E%3C/svg%3E'
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#f3f4f6',
                                borderRadius: 1,
                                border: '1px solid #ddd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                No Image
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {(lot.lot_images?.length || lot.harvest_images?.length || 0)} images
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person fontSize="small" />
                          {lot.farmer_name}
                        </Box>
                      </TableCell>
                      <TableCell>{lot.quantity} {lot.unit}</TableCell>
                      <TableCell>${lot.reserve_price.toFixed(2)}</TableCell>
                      <TableCell>{lot.quality_grade}</TableCell>
                      <TableCell>
                        <Chip
                          label={lot.status}
                          color={getStatusColor(lot.status)}
                          size="small"
                          icon={getStatusIcon(lot.status) || undefined}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(lot.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewLot(lot)}
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditLot(lot)}
                          title="Edit Lot"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleStatus(lot)}
                          title={lot.status === 'active' ? 'Deactivate' : 'Approve Lot'}
                          color={lot.status === 'active' ? 'error' : 'success'}
                        >
                          {lot.status === 'active' ? <Cancel /> : <CheckCircle />}
                        </IconButton>
                        {lot.status === 'scheduled' && lot.auction_start_date && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFromAuctionClick(lot)}
                            title="Remove from Auction"
                            color="warning"
                          >
                            <RemoveCircle />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* View Lot Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Lot Details</DialogTitle>
        <DialogContent>
          {selectedLot && (
            <Box>
              {/* Lot Images */}
              {(selectedLot.lot_images && selectedLot.lot_images.length > 0 || selectedLot.harvest_images && selectedLot.harvest_images.length > 0) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Lot Images
                  </Typography>
                  <Grid container spacing={2}>
                    {(selectedLot.lot_images || selectedLot.harvest_images || []).map((img: any, index: number) => {
                      const imageUrl = typeof img === 'string' ? img : img.url
                      const fullUrl = imageUrl?.startsWith('http') ? imageUrl : `http://localhost:3000${imageUrl}`
                      return (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Box
                            component="img"
                            src={fullUrl}
                            alt={`Lot image ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: 150,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #ddd',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                            onClick={() => handleOpenImageOverlay(selectedLot!, index)}
                            onError={(e: any) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E'
                            }}
                          />
                        </Grid>
                      )
                    })}
                  </Grid>
                </Box>
              )}

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Lot Name</Typography>
                  <Typography variant="body1">{selectedLot.lot_name}</Typography>
                </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Farmer</Typography>
                <Typography variant="body1">{selectedLot.farmer_name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                <Typography variant="body1">{selectedLot.lot_description || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">Quantity</Typography>
                <Typography variant="body1">{selectedLot.quantity} {selectedLot.unit}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">Reserve Price</Typography>
                <Typography variant="body1">${selectedLot.reserve_price.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">Starting Price</Typography>
                <Typography variant="body1">${(selectedLot.starting_price || 0).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">Quality Grade</Typography>
                <Typography variant="body1">{selectedLot.quality_grade}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Chip label={selectedLot.status} color={getStatusColor(selectedLot.status)} size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Inspection Location</Typography>
                <Typography variant="body1">{selectedLot.inspection_location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Pickup Location</Typography>
                <Typography variant="body1">{selectedLot.pickup_location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Payment Terms</Typography>
                <Typography variant="body1">{selectedLot.payment_terms || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Delivery Terms</Typography>
                <Typography variant="body1">{selectedLot.delivery_terms || 'N/A'}</Typography>
              </Grid>
            </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lot Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Lot</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Lot Name"
                value={editData.lot_name || ''}
                onChange={(e) => setEditData({ ...editData, lot_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editData.lot_description || ''}
                onChange={(e) => setEditData({ ...editData, lot_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={editData.quantity || ''}
                onChange={(e) => setEditData({ ...editData, quantity: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Unit"
                value={editData.unit || ''}
                onChange={(e) => setEditData({ ...editData, unit: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Reserve Price"
                type="number"
                value={editData.reserve_price || ''}
                onChange={(e) => setEditData({ ...editData, reserve_price: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Starting Price"
                type="number"
                value={editData.starting_price || ''}
                onChange={(e) => setEditData({ ...editData, starting_price: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quality Grade"
                value={editData.quality_grade || ''}
                onChange={(e) => setEditData({ ...editData, quality_grade: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Inspection Location"
                value={editData.inspection_location || ''}
                onChange={(e) => setEditData({ ...editData, inspection_location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pickup Location"
                value={editData.pickup_location || ''}
                onChange={(e) => setEditData({ ...editData, pickup_location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Terms"
                multiline
                rows={2}
                value={editData.payment_terms || ''}
                onChange={(e) => setEditData({ ...editData, payment_terms: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Delivery Terms"
                multiline
                rows={2}
                value={editData.delivery_terms || ''}
                onChange={(e) => setEditData({ ...editData, delivery_terms: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateLot} variant="contained" color="primary">
            Update Lot
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, lot: null })}>
        <DialogTitle>Remove from Auction</DialogTitle>
        <DialogContent>
          <Typography>
            Remove "{confirmDialog.lot?.lot_name}" from its current auction? This will allow it to be added to another auction.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, lot: null })}>Cancel</Button>
          <Button onClick={handleRemoveFromAuction} variant="contained" color="warning">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Overlay */}
      <ImageOverlay
        open={imageOverlayOpen}
        onClose={() => setImageOverlayOpen(false)}
        images={overlayImages}
        currentImageIndex={currentImageIndex}
        onImageChange={setCurrentImageIndex}
      />

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}

export default Lots
